// Dashboards section — renders inside the shared AppShell chrome.
// Two-column layout matching v1: dashboards list on the left,
// selected dashboard's insights on the right. Detail view (single
// dashboard rename / share / remove insight) shows when a dashboard
// is selected; "All dashboards" is the empty-selection state with
// create-new affordance.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  createDashboard,
  deleteDashboard,
  fetchDashboards,
  getDashboard,
  refreshInsight,
  removeInsight,
  renameDashboard,
  toggleOrgShare,
  type DashboardDetail,
  type DashboardSummary,
  type InsightView,
} from "../../api/dashboards";
import { ChartCard } from "../charts/ChartCard";
import type { ChartSpec } from "../charts/types";

interface Props {
  bearer: string;
  organization: string;
}

export function DashboardsPage({ bearer, organization }: Props) {
  const path = window.location.pathname;
  const idMatch = /^\/dashboards\/(\d+)/.exec(path);
  const activeId = idMatch ? Number(idMatch[1]) : null;

  const [items, setItems] = useState<DashboardSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Lifted up so the left sidebar can show an INSIGHTS sublist for
  // the currently-open dashboard. The detail view shares this state
  // via the `detail` prop and the `setDetail` setter.
  const [activeDetail, setActiveDetail] = useState<DashboardDetail | null>(null);

  // Server already org-scopes the list when we pass ?organization=,
  // so the user only ever sees the active org's dashboards.
  const reloadList = useCallback(() => {
    if (!organization) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchDashboards(bearer, organization)
      .then((d) => {
        setItems(d);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [bearer, organization]);

  useEffect(() => reloadList(), [reloadList]);

  // Auto-open the first dashboard in the active org when the user
  // lands on /dashboards with no specific id in the URL — prefers
  // the org's default dashboard (is_default) when one exists.
  useEffect(() => {
    if (loading || activeId !== null || items.length === 0) return;
    const target = items.find((d) => d.is_default) ?? items[0];
    if (!target) return;
    window.history.replaceState(null, "", `/dashboards/${target.id}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [loading, activeId, items]);

  // When the user switches orgs while on a dashboard from the prior
  // org, bounce them back to the list so they don't sit on a 403.
  useEffect(() => {
    if (activeId == null || loading) return;
    if (items.some((d) => d.id === activeId)) return;
    window.history.replaceState(null, "", "/dashboards");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [activeId, items, loading]);

  return (
    <div className="flex flex-1 min-h-[calc(100vh-70px)]">
      <DashboardsListPanel
        items={items}
        loading={loading}
        activeId={activeId}
        activeInsights={activeDetail?.id === activeId ? activeDetail.insights : []}
        activeShared={activeDetail?.id === activeId ? activeDetail.shared_with_org : false}
        onCreate={async (name) => {
          if (!organization) {
            setError("Pick an organisation in the header dropdown first.");
            return;
          }
          try {
            const fresh = await createDashboard(bearer, name, organization);
            setItems((prev) => [fresh, ...prev]);
            window.history.pushState(null, "", `/dashboards/${fresh.id}`);
            window.dispatchEvent(new PopStateEvent("popstate"));
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
          }
        }}
        onDelete={async (id) => {
          try {
            await deleteDashboard(bearer, id);
            setItems((prev) => prev.filter((x) => x.id !== id));
            if (activeId === id) {
              window.history.pushState(null, "", "/dashboards");
              window.dispatchEvent(new PopStateEvent("popstate"));
            }
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
          }
        }}
      />
      <div className="flex-1 min-w-0 overflow-y-auto px-6 py-8">
        {error && (
          <div className="mb-4 rounded-[12px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            {error}
          </div>
        )}
        {activeId == null ? (
          <DashboardEmptyState count={items.length} />
        ) : (
          <DashboardDetailView
            key={activeId}
            bearer={bearer}
            id={activeId}
            onDetailChange={setActiveDetail}
            onRenamed={(fresh) =>
              setItems((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)))
            }
            onDeleted={() => {
              setItems((prev) => prev.filter((x) => x.id !== activeId));
              setActiveDetail(null);
              window.history.pushState(null, "", "/dashboards");
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Left column — list + create                                          */
/* ------------------------------------------------------------------ */

interface ListProps {
  items: DashboardSummary[];
  loading: boolean;
  activeId: number | null;
  activeInsights: InsightView[];
  activeShared: boolean;
  onCreate: (name: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

function DashboardsListPanel({
  items,
  loading,
  activeId,
  activeInsights,
  activeShared,
  onCreate,
  onDelete,
}: ListProps) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <aside className="w-[260px] shrink-0 border-r border-line bg-white py-5 px-3 overflow-y-auto">
      <div className="flex items-center justify-between px-2 mb-3">
        <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-neutral-500">
          My dashboards
        </div>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-500 hover:bg-chip hover:text-neutral-900"
          aria-label="New dashboard"
          title="New dashboard"
        >
          <PlusIcon />
        </button>
      </div>
      {creating && (
        <div className="px-2 mb-2 space-y-1.5">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Dashboard name"
            className="w-full h-9 px-3 rounded-md border border-line text-[13px] outline-none focus:ring-1 focus:ring-brand"
            onKeyDown={async (e) => {
              if (e.key === "Enter" && name.trim()) {
                setBusy(true);
                await onCreate(name.trim());
                setBusy(false);
                setName("");
                setCreating(false);
              } else if (e.key === "Escape") {
                setCreating(false);
                setName("");
              }
            }}
          />
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setName("");
              }}
              className="text-[12px] text-neutral-500 hover:text-neutral-800 px-2 h-7"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !name.trim()}
              onClick={async () => {
                setBusy(true);
                await onCreate(name.trim());
                setBusy(false);
                setName("");
                setCreating(false);
              }}
              className="text-[12px] bg-brand text-white rounded-md px-3 h-7 hover:bg-brand-600 disabled:opacity-40"
            >
              Create
            </button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="px-3 text-[12px] text-neutral-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="px-3 text-[12px] text-neutral-400 leading-relaxed">
          No dashboards yet. Add a chart from the Search tab via the “Add to dashboard” icon.
        </div>
      ) : (
        <ul className="space-y-px">
          {items.map((d) => {
            const isActive = activeId === d.id;
            return (
              <li key={d.id}>
                <DashboardRow
                  d={d}
                  active={isActive}
                  // Active-only shared state is more accurate than the
                  // stale `d.shared_with_org` (which the list cache
                  // doesn't refresh after a toggle); for the rest we
                  // fall back to the list value.
                  sharedWithOrg={isActive ? activeShared : d.shared_with_org}
                  onDelete={() => onDelete(d.id)}
                />
                {isActive && activeInsights.length > 0 && (
                  <ul className="mt-0.5 mb-1.5 ml-3 border-l border-line/70">
                    <li className="px-3 py-1 text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-400">
                      Insights
                    </li>
                    {activeInsights.map((i) => (
                      <li key={i.id}>
                        <a
                          href={`#insight-${i.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            const el = document.getElementById(`insight-${i.id}`);
                            el?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                          className="block px-3 py-1.5 text-[12.5px] text-neutral-700 hover:text-neutral-900 hover:bg-chip/60 rounded-md truncate"
                          title={i.name ?? i.user_query}
                        >
                          {i.name || i.user_query || "Untitled insight"}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

function DashboardRow({
  d,
  active,
  sharedWithOrg,
  onDelete: _onDelete,
}: {
  d: DashboardSummary;
  active: boolean;
  sharedWithOrg: boolean;
  onDelete: () => void;
}) {
  return (
    <div
      className={
        "flex items-center gap-1.5 rounded-md transition-colors " +
        (active ? "bg-chip" : "hover:bg-chip/60")
      }
    >
      <a
        href={`/dashboards/${d.id}`}
        onClick={(e) => {
          e.preventDefault();
          window.history.pushState(null, "", `/dashboards/${d.id}`);
          window.dispatchEvent(new PopStateEvent("popstate"));
        }}
        className={
          "flex-1 min-w-0 px-3 py-2 text-[13px] truncate flex items-center gap-2 " +
          (active ? "text-neutral-900 font-medium" : "text-neutral-700")
        }
        title={d.name}
      >
        <span className="flex-1 min-w-0 truncate">{d.name}</span>
        {sharedWithOrg && <PersonIcon />}
      </a>
    </div>
  );
}

function DashboardEmptyState({ count }: { count: number }) {
  if (count === 0) {
    return (
      <div className="max-w-md mx-auto mt-24 text-center text-neutral-500">
        <div className="text-[16px] font-medium text-neutral-700 mb-2">No dashboards yet</div>
        <div className="text-[13px] leading-relaxed">
          When you find an answer you want to keep, hit the “Add to dashboard” icon on the chart
          card. The new dashboard will show up here.
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-md mx-auto mt-24 text-center text-neutral-500">
      <div className="text-[16px] font-medium text-neutral-700 mb-2">Pick a dashboard</div>
      <div className="text-[13px]">Select one from the list on the left to open it.</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Right column — single-dashboard view                                 */
/* ------------------------------------------------------------------ */

interface DetailProps {
  bearer: string;
  id: number;
  onRenamed: (d: DashboardSummary) => void;
  onDeleted: () => void;
  onDetailChange: (d: DashboardDetail | null) => void;
}

function DashboardDetailView({ bearer, id, onRenamed, onDeleted, onDetailChange }: DetailProps) {
  const [detail, setDetail] = useState<DashboardDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState("");
  // Anchored popover state — share menu and the triple-dot menu.
  const [shareOpen, setShareOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const shareBtnRef = useRef<HTMLButtonElement | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement | null>(null);
  // Insights currently being refreshed against the warehouse. The
  // dashboard pulls the cached rows first (snappy first paint) and
  // immediately kicks off a re-execute per insight so the user always
  // sees today's numbers.
  const [refreshing, setRefreshing] = useState<Set<number>>(new Set());

  // Keep the page-level detail in sync so the sidebar can render the
  // INSIGHTS sublist + the row's shared/person icon.
  useEffect(() => {
    onDetailChange(detail);
  }, [detail, onDetailChange]);

  const reload = useCallback(() => {
    getDashboard(bearer, id)
      .then((d) => {
        setDetail(d);
        setError(null);
        // Kick off a per-insight refresh in parallel. Each one swaps
        // into local state as it lands; errors are non-fatal (stored
        // rows remain visible).
        const ids = d.insights.map((i) => i.id);
        if (ids.length > 0) {
          setRefreshing(new Set(ids));
          for (const insightId of ids) {
            void refreshInsight(bearer, id, insightId)
              .then((fresh) => {
                setDetail((prev) =>
                  prev
                    ? {
                        ...prev,
                        insights: prev.insights.map((x) => (x.id === fresh.id ? fresh : x)),
                      }
                    : prev,
                );
              })
              .catch(() => {
                /* keep stored rows on error */
              })
              .finally(() => {
                setRefreshing((prev) => {
                  const next = new Set(prev);
                  next.delete(insightId);
                  return next;
                });
              });
          }
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [bearer, id]);

  useEffect(() => reload(), [reload]);

  if (error) {
    return <div className="text-sm text-red-700">{error}</div>;
  }
  if (!detail) {
    return <div className="text-sm text-neutral-500">Loading…</div>;
  }


  const setSharedFlag = async (next: boolean) => {
    try {
      await toggleOrgShare(bearer, id, next);
      setDetail({ ...detail, shared_with_org: next } as DashboardDetail);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {renaming ? (
            <input
              autoFocus
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={async () => {
                const next = draftName.trim();
                if (next && next !== detail.name) {
                  try {
                    const fresh = await renameDashboard(bearer, id, next);
                    setDetail({ ...detail, name: fresh.name });
                    onRenamed(fresh);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  }
                }
                setRenaming(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") setRenaming(false);
              }}
              className="text-[22px] font-semibold tracking-tight border-b border-brand outline-none bg-transparent min-w-0"
            />
          ) : (
            <h1 className="text-[22px] font-semibold tracking-tight text-neutral-900 truncate">
              {detail.name}
            </h1>
          )}
          {detail.shared_with_org && (
            <span className="text-[11px] font-medium text-brand bg-brand/10 rounded-full px-2.5 py-1">
              Shared
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {refreshing.size > 0 && (
            <span className="text-[11px] text-neutral-400 italic mr-2">
              Refreshing {refreshing.size}…
            </span>
          )}
          <div className="relative">
            <button
              ref={shareBtnRef}
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setShareOpen((v) => !v);
              }}
              className={
                "h-8 w-8 inline-flex items-center justify-center rounded-full transition-colors " +
                (shareOpen ? "bg-chip text-brand" : "text-neutral-500 hover:bg-chip hover:text-neutral-800")
              }
              aria-label="Share dashboard"
              title="Share dashboard"
            >
              <ShareLinkIcon />
            </button>
            {shareOpen && (
              <SharePopover
                shared={detail.shared_with_org}
                onToggle={(next) => void setSharedFlag(next)}
                anchorRef={shareBtnRef}
                onClose={() => setShareOpen(false)}
              />
            )}
          </div>
          <div className="relative">
            <button
              ref={menuBtnRef}
              type="button"
              onClick={() => {
                setShareOpen(false);
                setMenuOpen((v) => !v);
              }}
              className={
                "h-8 w-8 inline-flex items-center justify-center rounded-full transition-colors " +
                (menuOpen ? "bg-chip text-neutral-800" : "text-neutral-500 hover:bg-chip hover:text-neutral-800")
              }
              aria-label="More actions"
              title="More actions"
            >
              <KebabIcon />
            </button>
            {menuOpen && (
              <ActionsMenu
                anchorRef={menuBtnRef}
                onClose={() => setMenuOpen(false)}
                onRename={() => {
                  setDraftName(detail.name);
                  setRenaming(true);
                  setMenuOpen(false);
                }}
                onDelete={async () => {
                  setMenuOpen(false);
                  try {
                    await deleteDashboard(bearer, id);
                    onDeleted();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>

      {detail.insights.length === 0 ? (
        <div className="text-sm text-neutral-500">No insights on this dashboard yet.</div>
      ) : (
        <div className="space-y-6">
          {detail.insights.map((i) => (
            <InsightRow
              key={i.id}
              insight={i}
              onRemove={async () => {
                try {
                  await removeInsight(bearer, id, i.id);
                  reload();
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InsightRow({ insight, onRemove }: { insight: InsightView; onRemove: () => void }) {
  const [confirming, setConfirming] = useState(false);
  // Legacy insights stored their title on insights.insight_name (not in
  // query_template). Splice it into the spec so the ChartCard header
  // shows the right label.
  const spec = {
    ...insight.chart_spec,
    title: insight.chart_spec?.title || insight.name || insight.user_query || "Untitled insight",
  } as ChartSpec;
  return (
    <div id={`insight-${insight.id}`} className="group/insight space-y-2 scroll-mt-20">
      <div className="flex items-center justify-between min-h-[20px]">
        <div className="text-[13px] text-neutral-500 truncate pr-3">{insight.user_query}</div>
        {confirming ? (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                onRemove();
              }}
              className="text-[12px] text-red-600 hover:underline"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="text-[12px] text-neutral-400 hover:text-neutral-700"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-red-600 opacity-0 group-hover/insight:opacity-100 transition-opacity shrink-0"
            aria-label="Remove from dashboard"
            title="Remove from dashboard"
          >
            <TrashIcon />
          </button>
        )}
      </div>
      <ChartCard spec={spec} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Local icons                                                          */
/* ------------------------------------------------------------------ */

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 3v10M3 8h10" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4l1 9h4l1-9" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-3.5 w-3.5 text-neutral-400 shrink-0">
      <circle cx="12" cy="8" r="3.5" />
      <path strokeLinecap="round" d="M4.5 20c1.4-3.5 4.3-5 7.5-5s6.1 1.5 7.5 5" />
    </svg>
  );
}

function ShareLinkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-[18px] w-[18px]">
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8 11l8-4M8 13l8 4" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-[18px] w-[18px]">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 text-brand">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.5l3.5 3.5L13 5" />
    </svg>
  );
}

function RenameIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4 text-neutral-500">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 20h4l10-10-4-4L4 16v4zM14 6l4 4" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* SharePopover — anchored to the share icon; "Share with org" toggle.  */
/* ------------------------------------------------------------------ */

interface SharePopoverProps {
  shared: boolean;
  onToggle: (next: boolean) => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

function SharePopover({ shared, onToggle, anchorRef, onClose }: SharePopoverProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [anchorRef, onClose]);

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-[240px] rounded-[14px] bg-white border border-line shadow-card z-40 overflow-hidden"
    >
      <div className="px-4 pt-3 pb-1.5 text-[12px] uppercase tracking-[0.06em] font-semibold text-neutral-500">
        Share with organization
      </div>
      <button
        type="button"
        onClick={() => onToggle(!shared)}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-[14px] text-neutral-900 hover:bg-chip transition-colors"
      >
        <PersonIcon />
        <span className="flex-1">All members</span>
        {shared && <CheckIcon />}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ActionsMenu — Rename / Delete, anchored to the kebab icon.           */
/* ------------------------------------------------------------------ */

interface ActionsMenuProps {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onRename: () => void;
  onDelete: () => void;
}

function ActionsMenu({ anchorRef, onClose, onRename, onDelete }: ActionsMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [anchorRef, onClose]);

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-[180px] rounded-[14px] bg-white border border-line shadow-card z-40 overflow-hidden"
    >
      <button
        type="button"
        onClick={onRename}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-[14px] text-neutral-900 hover:bg-chip transition-colors"
      >
        <RenameIcon />
        <span>Rename</span>
      </button>
      <div className="border-t border-line/70" />
      {confirmingDelete ? (
        <div className="flex items-center justify-between px-4 py-2.5 text-[13px]">
          <span className="text-neutral-700">Delete?</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onDelete}
              className="text-red-600 font-medium hover:underline"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="text-neutral-400 hover:text-neutral-700"
            >
              No
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-[14px] text-red-600 hover:bg-red-50 transition-colors"
        >
          <TrashIcon />
          <span>Delete</span>
        </button>
      )}
    </div>
  );
}


